import React, { useEffect, useMemo, useRef, useState } from 'react';
import { resolveProductIconPath, resolveProductIconTone } from '../productVisuals';

const MOVEMENT_META = {
  purchase_pending_created: {
    label: 'Compra registrada',
    tone: 'border-amber-200 bg-amber-50 text-amber-700',
    rowTone: 'bg-amber-50/55 hover:bg-amber-50/80',
    lane: 'inbound',
    commercial: true,
  },
  purchase_assigned_to_route: {
    label: 'Retiro asignado',
    tone: 'border-sky-200 bg-sky-50 text-sky-700',
    rowTone: 'bg-sky-50/55 hover:bg-sky-50/80',
    lane: 'inbound',
    commercial: false,
  },
  purchase_pickup_started: {
    label: 'Retiro en curso',
    tone: 'border-cyan-200 bg-cyan-50 text-cyan-700',
    rowTone: 'bg-cyan-50/55 hover:bg-cyan-50/80',
    lane: 'inbound',
    commercial: false,
    blink: true,
  },
  purchase_received: {
    label: 'Ingreso validado',
    tone: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    rowTone: 'bg-emerald-50/55 hover:bg-emerald-50/80',
    lane: 'inbound',
    commercial: true,
  },
  stock_outbound_prepared: {
    label: 'Pedido de salida listo',
    tone: 'border-violet-200 bg-violet-50 text-violet-700',
    rowTone: 'bg-violet-50/55 hover:bg-violet-50/80',
    lane: 'outbound',
    commercial: true,
  },
  stock_outbound_reserved: {
    label: 'Salida reservada',
    tone: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    rowTone: 'bg-indigo-50/55 hover:bg-indigo-50/80',
    lane: 'outbound',
    commercial: false,
  },
  stock_outbound_assigned_to_route: {
    label: 'Entrega asignada',
    tone: 'border-sky-200 bg-sky-50 text-sky-700',
    rowTone: 'bg-sky-50/55 hover:bg-sky-50/80',
    lane: 'outbound',
    commercial: false,
  },
  stock_outbound_started: {
    label: 'Salida en tránsito',
    tone: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700',
    rowTone: 'bg-fuchsia-50/55 hover:bg-fuchsia-50/80',
    lane: 'outbound',
    commercial: false,
    blink: true,
  },
  stock_outbound_delivered: {
    label: 'Entrega completada',
    tone: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    rowTone: 'bg-emerald-50/55 hover:bg-emerald-50/80',
    lane: 'outbound',
    commercial: true,
  },
  stock_outbound_cancelled: {
    label: 'Salida cancelada',
    tone: 'border-rose-200 bg-rose-50 text-rose-700',
    rowTone: 'bg-rose-50/55 hover:bg-rose-50/80',
    lane: 'outbound',
    commercial: true,
  },
  stock_outbound_replanned: {
    label: 'Salida reprogramada',
    tone: 'border-orange-200 bg-orange-50 text-orange-700',
    rowTone: 'bg-orange-50/55 hover:bg-orange-50/80',
    lane: 'outbound',
    commercial: false,
  },
  stock_adjustment: {
    label: 'Ajuste',
    tone: 'border-slate-200 bg-slate-50 text-slate-700',
    rowTone: 'bg-slate-50/70 hover:bg-slate-100/90',
    lane: 'special',
    commercial: true,
  },
};

const FILTER_BUTTON_CLASS = (active) =>
  [
    'rounded-full border px-3 py-1.5 text-xs font-semibold transition-all duration-200',
    active
      ? 'border-slate-900 bg-slate-950 text-white shadow-[0_12px_24px_rgba(15,23,42,0.18)]'
      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900',
  ].join(' ');

const getMovementTimestampMs = (entry = {}) => {
  const sources = [entry.appliedAt, entry.createdAt, entry.updatedAt, entry.endTime, entry.startTime];
  for (const source of sources) {
    if (!source) continue;
    if (typeof source?.toDate === 'function') {
      try {
        return source.toDate().getTime();
      } catch {
        continue;
      }
    }
    const parsed = new Date(source);
    if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
  }
  return 0;
};

const formatDateTime = (value) => {
  const ms = getMovementTimestampMs({ appliedAt: value });
  if (!ms) return 'Sin fecha';
  return new Date(ms).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMoney = (value) => {
  const amount = Number(value || 0);
  if (!amount) return '—';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatOperationReason = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  const labels = {
    sale: 'Venta',
    warranty: 'Garantía',
    guarantee: 'Garantía',
    repair: 'Reparación',
    replacement: 'Reposición',
    transfer: 'Traslado',
    loan: 'Préstamo',
    return: 'Devolución',
    storage: 'Guardado',
  };
  return labels[normalized] || normalized.replace(/_/g, ' ');
};

const formatStorageDays = (days) => {
  if (!Number.isFinite(days) || days == null || days < 0) return '—';
  if (days === 0) return 'El mismo día';
  if (days === 1) return '1 día';
  return `${days} días`;
};

const resolveMeta = (movement) =>
  MOVEMENT_META[movement?.movementType] || {
    label: String(movement?.movementType || 'Movimiento').replace(/_/g, ' '),
    tone: 'border-slate-200 bg-slate-50 text-slate-700',
    rowTone: 'bg-slate-50/70 hover:bg-slate-100/90',
    lane: 'special',
    commercial: false,
    blink: false,
  };

const movementIconConfig = {
  inbound: {
    label: 'Entrada',
    className: 'border-emerald-200 bg-emerald-100 text-emerald-700',
    path: 'M12 3.75v10.5m0 0l-4.5-4.5m4.5 4.5l4.5-4.5M4.5 18.75h15',
  },
  outbound: {
    label: 'Salida',
    className: 'border-fuchsia-200 bg-fuchsia-100 text-fuchsia-700',
    path: 'M4.5 12h12m0 0l-4.5-4.5m4.5 4.5l-4.5 4.5M19.5 5.25v13.5',
  },
  special: {
    label: 'Especial',
    className: 'border-orange-200 bg-orange-100 text-orange-700',
    path: 'M4.5 7.5h15M7.5 7.5v9m9-9v9M4.5 16.5h15',
  },
};

const MovementDirectionIcon = ({ meta }) => {
  const icon = movementIconConfig[meta?.lane] || movementIconConfig.special;

  return (
    <span
      title={icon.label}
      className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border shadow-sm ${icon.className} ${meta?.blink ? 'motion-safe:animate-pulse' : ''}`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="h-[18px] w-[18px]">
        <path strokeLinecap="round" strokeLinejoin="round" d={icon.path} />
      </svg>
    </span>
  );
};

const resolveMovementCounterparty = (movement) =>
  movement.customerNameSnapshot ||
  movement.destinationNameSnapshot ||
  movement.supplierNameSnapshot ||
  movement.contactNameSnapshot ||
  'Sin contraparte';

const buildMovementProductVisual = (movement = {}, categoryById = {}) => ({
  name: movement.productNameSnapshot || 'Producto sin nombre',
  sku: movement.productSkuSnapshot || '',
  category: movement.productCategorySnapshot || categoryById[movement.productId] || '',
  model: movement.productModelSnapshot || '',
  brand: movement.productBrandSnapshot || '',
  featureLabel: movement.productFeatureSnapshot || '',
});

const normalizeLookupText = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

const resolveMovementTrace = (movement) => {
  const parts = [];
  if (movement.routeCodeSnapshot) {
    const routeLabel = String(movement.routeCodeSnapshot || '').trim();
    parts.push(/^ruta\b/i.test(routeLabel) ? routeLabel : `Ruta ${routeLabel}`);
  }
  if (movement.transportNameSnapshot) parts.push(movement.transportNameSnapshot);
  if (movement.driverNameSnapshot) parts.push(movement.driverNameSnapshot);
  if (movement.referenceCodeSnapshot) parts.push(`Ref. ${movement.referenceCodeSnapshot}`);
  return parts.join(' · ') || 'Sin traza adicional';
};

const isSpecialTraceMovement = (movement, meta) => {
  const normalizedReason = String(movement?.operationReason || movement?.operationType || '').trim().toLowerCase();
  if (meta?.lane === 'special') return true;
  return ['warranty', 'guarantee', 'repair', 'return'].includes(normalizedReason);
};

const buildSummaryCard = (label, value, helper, tone) => ({ label, value, helper, tone });
const buildInsightCard = (label, title, helper, tone) => ({ label, title, helper, tone });

const buildTraceInsightsByMovement = (movements = []) => {
  const traceById = new Map();
  const lastInboundByProduct = new Map();
  const lastPurchaseSourceByProduct = new Map();
  const lastPreparedOutboundBySignature = new Map();
  const lastPreparedOutboundByLooseSignature = new Map();
  const lastPreparedOutboundByOrderId = new Map();
  const lastPreparedOutboundByOrderLineId = new Map();

  const buildOutboundSignature = (movement = {}, includeReference = true) => {
    const productId = String(movement?.productId || '').trim().toLowerCase();
    const counterparty = normalizeLookupText(
      movement?.customerNameSnapshot ||
      movement?.destinationNameSnapshot ||
      movement?.contactNameSnapshot ||
      ''
    );
    const reason = normalizeLookupText(movement?.operationReason || movement?.operationType || '');
    const reference = includeReference
      ? normalizeLookupText(movement?.referenceCodeSnapshot || '')
      : '';
    return [productId, counterparty, reason, reference].join('::');
  };

  [...movements]
    .slice()
    .sort((a, b) => getMovementTimestampMs(a) - getMovementTimestampMs(b))
    .forEach((movement) => {
      const movementId = String(movement?.id || '').trim();
      if (!movementId) return;

      const movementType = String(movement?.movementType || '').trim().toLowerCase();
      const productId = String(movement?.productId || '').trim();
      const meta = resolveMeta(movement);
      const appliedMs = getMovementTimestampMs(movement);
      const lastInbound =
        productId
          ? lastPurchaseSourceByProduct.get(productId)
            || lastInboundByProduct.get(productId)
            || null
          : null;
      const lastInboundMs = lastInbound ? getMovementTimestampMs(lastInbound) : 0;
      const storageDays =
        meta.lane === 'outbound' && appliedMs && lastInboundMs
          ? Math.max(0, Math.floor((appliedMs - lastInboundMs) / 86400000))
          : null;
      const outboundSignature = buildOutboundSignature(movement, true);
      const outboundLooseSignature = buildOutboundSignature(movement, false);
      const outboundOrderLineId = String(movement?.stockOutboundOrderLineId || '').trim();
      const outboundOrderId = String(movement?.stockOutboundOrderId || '').trim();
      const lastPreparedOutbound = outboundSignature
        ? lastPreparedOutboundByOrderLineId.get(outboundOrderLineId)
          || lastPreparedOutboundByOrderId.get(outboundOrderId)
          || lastPreparedOutboundBySignature.get(outboundSignature)
          || lastPreparedOutboundByLooseSignature.get(outboundLooseSignature)
          || null
        : null;
      const fallbackCostUnit =
        movement.costUnitSnapshot ??
        movement.unitCostSnapshot ??
        movement.unitCost ??
        lastPreparedOutbound?.costUnitSnapshot ??
        lastPreparedOutbound?.unitCostSnapshot ??
        lastPreparedOutbound?.unitCost ??
        null;
      const fallbackSaleUnit =
        movement.saleUnitSnapshot ??
        lastPreparedOutbound?.saleUnitSnapshot ??
        lastPreparedOutbound?.saleUnitPrice ??
        null;
      const fallbackMargin =
        movement.marginSnapshot ??
        lastPreparedOutbound?.marginSnapshot ??
        null;
      const fallbackPurchaseNumber =
        movement.purchaseNumberSnapshot ||
        lastPreparedOutbound?.purchaseNumberSnapshot ||
        '';
      const fallbackBatchLabel =
        movement.batchLabelSnapshot ||
        lastPreparedOutbound?.batchLabelSnapshot ||
        '';
      const fallbackPurchaseId =
        movement.purchaseId ||
        lastPreparedOutbound?.purchaseId ||
        '';
      const fallbackPurchaseLineId =
        movement.purchaseLineId ||
        lastPreparedOutbound?.purchaseLineId ||
        '';

      traceById.set(movementId, {
        sourceLabel:
          movement.supplierNameSnapshot ||
          lastPreparedOutbound?.supplierNameSnapshot ||
          lastInbound?.supplierNameSnapshot ||
          lastInbound?.destinationNameSnapshot ||
          '',
        batchLabel:
          fallbackBatchLabel ||
          lastInbound?.batchLabelSnapshot ||
          '',
        sourceReference:
          fallbackBatchLabel ||
          fallbackPurchaseNumber ||
          fallbackPurchaseId ||
          lastInbound?.batchLabelSnapshot ||
          lastInbound?.purchaseNumberSnapshot ||
          lastInbound?.purchaseId ||
          lastInbound?.referenceCodeSnapshot ||
          '',
        inboundAt:
          movement.supplierNameSnapshot && meta.lane === 'inbound'
            ? appliedMs
            : lastInboundMs || null,
        storageDays,
        destinationLabel:
          movement.destinationNameSnapshot ||
          movement.customerNameSnapshot ||
          movement.contactNameSnapshot ||
          '',
        routeLabel: movement.routeCodeSnapshot || '',
        transportLabel: movement.transportNameSnapshot || '',
        driverLabel: movement.driverNameSnapshot || '',
        purchaseId: fallbackPurchaseId || lastInbound?.purchaseId || '',
        purchaseLineId: fallbackPurchaseLineId || lastInbound?.purchaseLineId || '',
        purchaseNumberSnapshot: fallbackPurchaseNumber || lastInbound?.purchaseNumberSnapshot || '',
        costUnit: fallbackCostUnit,
        saleUnit: fallbackSaleUnit,
        marginTotal: fallbackMargin,
      });

      if (
        productId &&
        meta.lane === 'inbound' &&
        ['purchase_received', 'purchase_pending_created', 'purchase_pending_adjusted'].includes(
          movementType
        )
      ) {
        lastInboundByProduct.set(productId, movement);
      }

      if (
        productId &&
        (
          movement.purchaseId ||
          movement.purchaseNumberSnapshot ||
          movement.batchLabelSnapshot ||
          movement.supplierNameSnapshot
        )
      ) {
        const previousPurchaseSource = lastPurchaseSourceByProduct.get(productId) || null;
        lastPurchaseSourceByProduct.set(productId, {
          ...(previousPurchaseSource || {}),
          ...movement,
          supplierNameSnapshot:
            movement.supplierNameSnapshot ||
            previousPurchaseSource?.supplierNameSnapshot ||
            '',
          purchaseId:
            movement.purchaseId ||
            previousPurchaseSource?.purchaseId ||
            '',
          purchaseLineId:
            movement.purchaseLineId ||
            previousPurchaseSource?.purchaseLineId ||
            '',
          purchaseNumberSnapshot:
            movement.purchaseNumberSnapshot ||
            previousPurchaseSource?.purchaseNumberSnapshot ||
            '',
          batchLabelSnapshot:
            movement.batchLabelSnapshot ||
            previousPurchaseSource?.batchLabelSnapshot ||
            '',
          costUnitSnapshot:
            movement.costUnitSnapshot ??
            previousPurchaseSource?.costUnitSnapshot ??
            null,
          costTotalSnapshot:
            movement.costTotalSnapshot ??
            previousPurchaseSource?.costTotalSnapshot ??
            null,
          appliedAt:
            movement.appliedAt ||
            movement.createdAt ||
            previousPurchaseSource?.appliedAt ||
            null,
        });
      }

      if (movementType === 'stock_outbound_prepared') {
        if (outboundSignature) {
          lastPreparedOutboundBySignature.set(outboundSignature, movement);
        }
        if (outboundLooseSignature) {
          lastPreparedOutboundByLooseSignature.set(outboundLooseSignature, movement);
        }
        if (outboundOrderId) {
          lastPreparedOutboundByOrderId.set(outboundOrderId, movement);
        }
        if (outboundOrderLineId) {
          lastPreparedOutboundByOrderLineId.set(outboundOrderLineId, movement);
        }
      }
    });

  return traceById;
};

const StockMovementsPanel = ({
  movements = [],
  products = [],
  focusMode = false,
  scrollHostRef = null,
  stickyTop = 0,
  onCondensedChange = null,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [laneFilter, setLaneFilter] = useState('all');
  const [viewMode, setViewMode] = useState('commercial');
  const [productFilter, setProductFilter] = useState('all');
  const [counterpartyFilter, setCounterpartyFilter] = useState('all');
  const [movementTypeFilter, setMovementTypeFilter] = useState('all');
  const [sortMode, setSortMode] = useState('recent');
  const [datePreset, setDatePreset] = useState('30');
  const [isCondensed, setIsCondensed] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [pinnedHeaderLayout, setPinnedHeaderLayout] = useState({
    active: false,
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  });
  const [pinnedTableHeadLayout, setPinnedTableHeadLayout] = useState({
    active: false,
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  });
  const headerSlotRef = useRef(null);
  const headerInnerRef = useRef(null);
  const tableHeadSlotRef = useRef(null);
  const tableHeadInnerRef = useRef(null);

  useEffect(() => {
    if (typeof onCondensedChange === 'function') {
      onCondensedChange(isCondensed);
    }
  }, [isCondensed, onCondensedChange]);

  useEffect(() => {
    if (!focusMode) {
      setIsCondensed(false);
      return undefined;
    }

    const scrollTarget =
      scrollHostRef?.current && typeof scrollHostRef.current.addEventListener === 'function'
        ? scrollHostRef.current
        : null;

    const handleScroll = () => {
      const scrollTop = Math.max(
        Number(window.scrollY || 0),
        Number(scrollTarget?.scrollTop || 0)
      );
      setIsCondensed(scrollTop > 8);
    };

    handleScroll();
    if (scrollTarget?.addEventListener) {
      scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (scrollTarget?.removeEventListener) {
        scrollTarget.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, [focusMode, scrollHostRef]);

  useEffect(() => {
    if (!focusMode) {
      setHeaderHeight(0);
      return undefined;
    }

    const measure = () => setHeaderHeight(headerInnerRef.current?.offsetHeight || 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [focusMode, isCondensed]);

  useEffect(() => {
    if (!focusMode) {
      setPinnedHeaderLayout((prev) => (prev.active ? { active: false, left: 0, top: 0, width: 0, height: 0 } : prev));
      setPinnedTableHeadLayout((prev) => (prev.active ? { active: false, left: 0, top: 0, width: 0, height: 0 } : prev));
      return undefined;
    }

    const scrollTarget =
      scrollHostRef?.current && typeof scrollHostRef.current.addEventListener === 'function'
        ? scrollHostRef.current
        : null;

    const updatePinning = () => {
      if (window.innerWidth < 1024) {
        setPinnedHeaderLayout((prev) => (prev.active ? { active: false, left: 0, top: 0, width: 0, height: 0 } : prev));
        setPinnedTableHeadLayout((prev) => (prev.active ? { active: false, left: 0, top: 0, width: 0, height: 0 } : prev));
        return;
      }

      const headerSlotRect = headerSlotRef.current?.getBoundingClientRect();
      const headerInnerHeight = headerInnerRef.current?.offsetHeight || 0;
      const tableHeadSlotRect = tableHeadSlotRef.current?.getBoundingClientRect();
      const tableHeadHeight = tableHeadInnerRef.current?.offsetHeight || 0;
      const scrollTop = Math.max(
        Number(window.scrollY || 0),
        Number(scrollTarget?.scrollTop || 0)
      );
      const targetTop = Math.max(stickyTop || 0, 0);
      const shouldPin = scrollTop > 28;

      const shouldPinHeader = Boolean(headerSlotRect) && shouldPin;
      if (!shouldPinHeader || !headerSlotRect) {
        setPinnedHeaderLayout((prev) => (prev.active ? { active: false, left: 0, top: 0, width: 0, height: 0 } : prev));
      } else {
        setPinnedHeaderLayout({
          active: true,
          left: headerSlotRect.left,
          top: targetTop,
          width: headerSlotRect.width,
          height: headerInnerHeight,
        });
      }

      const tableTargetTop = targetTop + (shouldPinHeader ? headerInnerHeight : headerSlotRect?.height || 0) + 18;
      const shouldPinTableHead = Boolean(tableHeadSlotRect) && shouldPin;
      if (!shouldPinTableHead || !tableHeadSlotRect) {
        setPinnedTableHeadLayout((prev) => (prev.active ? { active: false, left: 0, top: 0, width: 0, height: 0 } : prev));
      } else {
        setPinnedTableHeadLayout({
          active: true,
          left: tableHeadSlotRect.left,
          top: tableTargetTop,
          width: tableHeadSlotRect.width,
          height: tableHeadHeight,
        });
      }
    };

    updatePinning();
    if (scrollTarget?.addEventListener) {
      scrollTarget.addEventListener('scroll', updatePinning, { passive: true });
    }
    window.addEventListener('scroll', updatePinning, { passive: true });
    window.addEventListener('resize', updatePinning);

    return () => {
      if (scrollTarget?.removeEventListener) {
        scrollTarget.removeEventListener('scroll', updatePinning);
      }
      window.removeEventListener('scroll', updatePinning);
      window.removeEventListener('resize', updatePinning);
    };
  }, [focusMode, headerHeight, isCondensed, scrollHostRef, stickyTop]);

  const productCategoryById = useMemo(
    () =>
      products.reduce((acc, product) => {
        acc[product.id] = product.category || '';
        return acc;
      }, {}),
    [products]
  );

  const productNameById = useMemo(
    () =>
      products.reduce((acc, product) => {
        acc[product.id] = product.name || 'Producto';
        return acc;
      }, {}),
    [products]
  );

  const productOptions = useMemo(() => {
    const usedProductIds = new Set(movements.map((movement) => String(movement.productId || '')).filter(Boolean));
    return [...usedProductIds]
      .map((productId) => ({
        value: productId,
        label: productNameById[productId] || 'Producto',
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [movements, productNameById]);

  const counterpartyOptions = useMemo(() => {
    const seen = new Map();
    movements.forEach((movement) => {
      const label = resolveMovementCounterparty(movement);
      if (!label || label === 'Sin contraparte') return;
      const key = label.toLowerCase();
      if (!seen.has(key)) {
        seen.set(key, { value: key, label });
      }
    });
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [movements]);

  const movementTypeOptions = useMemo(() => {
    const seen = new Map();
    movements.forEach((movement) => {
      const meta = resolveMeta(movement);
      const key = String(movement.movementType || '').trim();
      if (!key) return;
      if (!seen.has(key)) {
        seen.set(key, { value: key, label: meta.label });
      }
    });
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [movements]);

  const traceInsightsByMovementId = useMemo(
    () => buildTraceInsightsByMovement(movements),
    [movements]
  );

  const filteredMovements = useMemo(() => {
    const normalizedSearch = String(searchTerm || '').trim().toLowerCase();
    const now = Date.now();
    const dateWindowMs =
      datePreset === 'all'
        ? 0
        : Number.isFinite(Number(datePreset))
          ? Number(datePreset) * 24 * 60 * 60 * 1000
          : 0;
    return movements.filter((movement) => {
      const meta = resolveMeta(movement);
      if (viewMode === 'commercial' && !meta.commercial) return false;
      if (laneFilter === 'special') {
        if (!isSpecialTraceMovement(movement, meta)) return false;
      } else if (laneFilter !== 'all' && meta.lane !== laneFilter) return false;
      if (productFilter !== 'all' && String(movement.productId || '') !== productFilter) return false;
      if (movementTypeFilter !== 'all' && String(movement.movementType || '') !== movementTypeFilter) return false;
      if (counterpartyFilter !== 'all' && resolveMovementCounterparty(movement).toLowerCase() !== counterpartyFilter) return false;
      if (dateWindowMs > 0) {
        const movementMs = getMovementTimestampMs(movement);
        if (!movementMs || now - movementMs > dateWindowMs) return false;
      }

      if (!normalizedSearch) return true;

      const haystack = [
        movement.productNameSnapshot,
        movement.productSkuSnapshot,
        productCategoryById[movement.productId],
        movement.supplierNameSnapshot,
        movement.customerNameSnapshot,
        movement.destinationNameSnapshot,
        movement.routeCodeSnapshot,
        movement.referenceCodeSnapshot,
        formatOperationReason(movement.operationReason || movement.operationType),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    }).sort((a, b) => {
      if (sortMode === 'oldest') return getMovementTimestampMs(a) - getMovementTimestampMs(b);
      if (sortMode === 'margin') {
        const aMargin = Number(a.marginSnapshot || 0);
        const bMargin = Number(b.marginSnapshot || 0);
        return bMargin - aMargin;
      }
      return getMovementTimestampMs(b) - getMovementTimestampMs(a);
    });
  }, [counterpartyFilter, datePreset, laneFilter, movementTypeFilter, movements, productCategoryById, productFilter, searchTerm, sortMode, viewMode]);

  const summaryCards = useMemo(() => {
    const inbound = filteredMovements.filter((movement) => resolveMeta(movement).lane === 'inbound');
    const outbound = filteredMovements.filter((movement) => resolveMeta(movement).lane === 'outbound');

    const inboundQty = inbound.reduce((acc, movement) => acc + Number(movement.quantity || 0), 0);
    const outboundQty = outbound.reduce((acc, movement) => acc + Number(movement.quantity || 0), 0);
    const inboundCost = inbound.reduce(
      (acc, movement) =>
        acc + Number(movement.costTotalSnapshot || Number(movement.unitCostSnapshot || movement.unitCost || 0) * Number(movement.quantity || 0)),
      0
    );
    const outboundSale = outbound.reduce(
      (acc, movement) =>
        acc + Number(movement.saleTotalSnapshot || Number(movement.saleUnitSnapshot || 0) * Number(movement.quantity || 0)),
      0
    );
    const outboundMargin = outbound.reduce(
      (acc, movement) =>
        acc + Number(movement.marginSnapshot || (Number(movement.saleUnitSnapshot || 0) - Number(movement.unitCostSnapshot || movement.unitCost || 0)) * Number(movement.quantity || 0)),
      0
    );

    return [
      buildSummaryCard('Ingresos', `${inboundQty}`, `${formatMoney(inboundCost)} en costo acumulado`, 'border-emerald-200 bg-emerald-50 text-emerald-700'),
      buildSummaryCard('Salidas', `${outboundQty}`, `${formatMoney(outboundSale)} en valor de salida`, 'border-violet-200 bg-violet-50 text-violet-700'),
      buildSummaryCard('Margen', formatMoney(outboundMargin), 'diferencia estimada sobre salidas visibles', 'border-cyan-200 bg-cyan-50 text-cyan-700'),
      buildSummaryCard('Eventos', `${filteredMovements.length}`, viewMode === 'commercial' ? 'lectura comercial activa' : 'traza operativa completa', 'border-slate-200 bg-slate-50 text-slate-700'),
    ];
  }, [filteredMovements, viewMode]);

  const quickInsights = useMemo(() => {
    if (filteredMovements.length === 0) {
      return [
        buildInsightCard('Producto con más movimiento', 'Sin datos', 'Ajustá filtros o esperá nuevos eventos.', 'border-slate-200 bg-slate-50 text-slate-700'),
        buildInsightCard('Contraparte más activa', 'Sin datos', 'Todavía no hay cruces visibles en esta lectura.', 'border-slate-200 bg-slate-50 text-slate-700'),
        buildInsightCard('Último movimiento', 'Sin datos', 'La planilla se completa apenas aparece actividad.', 'border-slate-200 bg-slate-50 text-slate-700'),
      ];
    }

    const productAgg = new Map();
    const counterpartyAgg = new Map();
    let latestMovement = filteredMovements[0] || null;

    filteredMovements.forEach((movement) => {
      const productLabel = movement.productNameSnapshot || 'Producto';
      const counterpartyLabel = resolveMovementCounterparty(movement);
      const qty = Number(movement.quantity || 0);
      const movementMs = getMovementTimestampMs(movement);

      productAgg.set(productLabel, (productAgg.get(productLabel) || 0) + qty);
      counterpartyAgg.set(counterpartyLabel, (counterpartyAgg.get(counterpartyLabel) || 0) + 1);

      if (!latestMovement || movementMs > getMovementTimestampMs(latestMovement)) {
        latestMovement = movement;
      }
    });

    const [topProduct = 'Sin datos', topProductQty = 0] = [...productAgg.entries()].sort((a, b) => b[1] - a[1])[0] || [];
    const [topCounterparty = 'Sin datos', topCounterpartyCount = 0] = [...counterpartyAgg.entries()].sort((a, b) => b[1] - a[1])[0] || [];
    const latestMeta = latestMovement ? resolveMeta(latestMovement) : null;

    return [
      buildInsightCard(
        'Producto con más movimiento',
        topProduct,
        topProductQty ? `${topProductQty} unidades visibles en esta lectura.` : 'Sin unidades visibles.',
        'border-cyan-200 bg-cyan-50 text-cyan-700'
      ),
      buildInsightCard(
        'Contraparte más activa',
        topCounterparty,
        topCounterpartyCount ? `${topCounterpartyCount} eventos relacionados en el rango actual.` : 'Todavía sin eventos relacionados.',
        'border-violet-200 bg-violet-50 text-violet-700'
      ),
      buildInsightCard(
        'Último movimiento',
        latestMovement?.productNameSnapshot || 'Sin datos',
        latestMovement ? `${latestMeta?.label || 'Movimiento'} · ${formatDateTime(latestMovement.appliedAt || latestMovement.createdAt)}` : 'Todavía no hay actividad reciente.',
        'border-emerald-200 bg-emerald-50 text-emerald-700'
      ),
    ];
  }, [filteredMovements]);

  const hasCustomFilters =
    searchTerm ||
    laneFilter !== 'all' ||
    viewMode !== 'commercial' ||
    productFilter !== 'all' ||
    counterpartyFilter !== 'all' ||
    movementTypeFilter !== 'all' ||
    sortMode !== 'recent' ||
    datePreset !== '30';

  const handleResetFilters = () => {
    setSearchTerm('');
    setLaneFilter('all');
    setViewMode('commercial');
    setProductFilter('all');
    setCounterpartyFilter('all');
    setMovementTypeFilter('all');
    setSortMode('recent');
    setDatePreset('30');
  };

  return (
    <div className={focusMode ? 'px-0 pb-6' : 'rounded-[30px] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-4 shadow-[0_18px_40px_rgba(15,23,42,0.08)] md:p-6'}>
      <div
        ref={headerSlotRef}
        style={pinnedHeaderLayout.active ? { height: `${pinnedHeaderLayout.height}px` } : undefined}
      >
      <div
        ref={headerInnerRef}
        className={
          focusMode
            ? `rounded-[24px] border border-slate-200 bg-white px-3 py-3 shadow-[0_18px_34px_rgba(15,23,42,0.10)] transition-all duration-200 md:px-4 ${isCondensed ? 'space-y-3' : 'space-y-4'}`
            : ''
        }
        style={pinnedHeaderLayout.active ? {
          position: 'fixed',
          top: `${pinnedHeaderLayout.top}px`,
          left: `${pinnedHeaderLayout.left}px`,
          width: `${pinnedHeaderLayout.width}px`,
          zIndex: 32,
        } : undefined}
      >
      <div className={`flex flex-col gap-4 ${!isCondensed ? 'border-b border-slate-200' : ''} ${isCondensed ? 'pb-0' : 'pb-5'} xl:flex-row xl:items-end xl:justify-between`}>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Movimiento general</p>
          <h2 className={`mt-1 font-bold text-slate-950 transition-all duration-200 ${isCondensed ? 'text-xl' : 'text-2xl'}`}>Ingresos, salidas y lectura comercial</h2>
          {!isCondensed ? (
            <>
              <p className="mt-2 max-w-3xl text-sm text-slate-600">
                Acá concentramos la planilla viva de compras, entregas y traza operativa para leer costo, salida, margen y destino sin salir del módulo.
              </p>
              <p className="mt-2 text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                Busca por producto, SKU, categoría, proveedor, cliente, ruta, referencia o motivo.
              </p>
            </>
          ) : (
            <p className="mt-2 text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
              Búsqueda fija por producto, contraparte, evento y rango.
            </p>
          )}
        </div>
          <div className="flex w-full flex-col gap-3 xl:max-w-2xl">
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar por producto, proveedor, cliente, ruta o referencia"
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-cyan-300 focus:ring-2 focus:ring-cyan-100"
            />
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setViewMode('commercial')} className={FILTER_BUTTON_CLASS(viewMode === 'commercial')}>Lectura comercial</button>
              <button type="button" onClick={() => setViewMode('operational')} className={FILTER_BUTTON_CLASS(viewMode === 'operational')}>Trazabilidad completa</button>
              <button type="button" onClick={() => setLaneFilter('all')} className={FILTER_BUTTON_CLASS(laneFilter === 'all')}>Todo</button>
              <button type="button" onClick={() => setLaneFilter('inbound')} className={FILTER_BUTTON_CLASS(laneFilter === 'inbound')}>Ingresos</button>
              <button type="button" onClick={() => setLaneFilter('outbound')} className={FILTER_BUTTON_CLASS(laneFilter === 'outbound')}>Salidas</button>
              <button type="button" onClick={() => setLaneFilter('special')} className={FILTER_BUTTON_CLASS(laneFilter === 'special')}>Especiales</button>
            </div>
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-5">
              <select
                value={datePreset}
                onChange={(event) => setDatePreset(event.target.value)}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-cyan-300 focus:ring-2 focus:ring-cyan-100"
              >
                <option value="7">Últimos 7 días</option>
                <option value="30">Últimos 30 días</option>
                <option value="90">Últimos 90 días</option>
                <option value="all">Todo el historial</option>
              </select>
              <select
                value={productFilter}
                onChange={(event) => setProductFilter(event.target.value)}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-cyan-300 focus:ring-2 focus:ring-cyan-100"
              >
                <option value="all">Todos los productos</option>
                {productOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <select
                value={counterpartyFilter}
                onChange={(event) => setCounterpartyFilter(event.target.value)}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-cyan-300 focus:ring-2 focus:ring-cyan-100"
              >
                <option value="all">Toda contraparte</option>
                {counterpartyOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <select
                value={movementTypeFilter}
                onChange={(event) => setMovementTypeFilter(event.target.value)}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-cyan-300 focus:ring-2 focus:ring-cyan-100"
              >
                <option value="all">Todos los eventos</option>
                {movementTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <select
                value={sortMode}
                onChange={(event) => setSortMode(event.target.value)}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 shadow-sm outline-none transition focus:border-cyan-300 focus:ring-2 focus:ring-cyan-100"
              >
                <option value="recent">Más recientes</option>
                <option value="oldest">Más antiguos</option>
                <option value="margin">Mayor margen</option>
              </select>
            </div>
            {hasCustomFilters ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Filtros activos
                </span>
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="rounded-full border border-slate-900 bg-slate-950 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white transition hover:bg-slate-800"
                >
                  Limpiar lectura
                </button>
              </div>
            ) : null}
          </div>
        </div>

      {!isCondensed ? (
        <>
          <div className="mt-5 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
            {summaryCards.map((card) => (
              <div key={card.label} className={`rounded-[20px] border shadow-sm ${card.tone} px-3.5 py-3`}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-75">{card.label}</p>
                <p className="mt-1.5 text-[1.35rem] font-bold leading-none">{card.value}</p>
                <p className="mt-1.5 text-[11px] opacity-80">{card.helper}</p>
              </div>
            ))}
          </div>

          <div className="mt-3 grid gap-2 lg:grid-cols-3">
            {quickInsights.map((card) => (
              <div key={card.label} className={`rounded-[20px] border shadow-sm ${card.tone} px-3.5 py-3`}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] opacity-75">{card.label}</p>
                <p className="mt-1.5 text-sm font-bold text-slate-950">{card.title}</p>
                <p className="mt-1.5 text-[11px] opacity-80">{card.helper}</p>
              </div>
            ))}
          </div>
        </>
      ) : null}
      </div>
      </div>

      <div className={`mt-5 ${focusMode ? 'border-t border-slate-200 bg-white' : 'overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm'}`}>
        <div
          ref={tableHeadSlotRef}
          style={pinnedTableHeadLayout.active ? { height: `${pinnedTableHeadLayout.height}px` } : undefined}
        >
        <div
          ref={tableHeadInnerRef}
          className={`hidden border-b border-slate-200 bg-slate-950 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-300 lg:grid lg:grid-cols-[140px_minmax(180px,1.1fr)_150px_88px_180px_120px_120px_120px_1fr] lg:gap-3 xl:px-5`}
          style={pinnedTableHeadLayout.active ? {
            position: 'fixed',
            top: `${pinnedTableHeadLayout.top}px`,
            left: `${pinnedTableHeadLayout.left}px`,
            width: `${pinnedTableHeadLayout.width}px`,
            zIndex: 30,
          } : undefined}
        >
          <span>Fecha</span>
          <span>Producto / operación</span>
          <span>Contraparte</span>
          <span>Cant.</span>
          <span>Tipo / motivo</span>
          <span>Costo</span>
          <span>Venta</span>
          <span>Margen</span>
          <span>Traza</span>
        </div>
        </div>

        <div className={focusMode ? 'overflow-visible' : 'max-h-[68vh] overflow-y-auto'}>
          {filteredMovements.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">
              No hay movimientos para esta lectura todavía.
            </div>
          ) : (
            filteredMovements.map((movement) => {
              const meta = resolveMeta(movement);
              const operationReasonLabel = formatOperationReason(movement.operationReason || movement.operationType);
              const traceInsights = traceInsightsByMovementId.get(movement.id) || {};
              const unitCost = Number(
                movement.costUnitSnapshot ||
                movement.unitCostSnapshot ||
                traceInsights.costUnit ||
                movement.unitCost ||
                0
              );
              const saleUnit = Number(
                movement.saleUnitSnapshot ||
                traceInsights.saleUnit ||
                0
              );
              const marginTotal = Number(
                movement.marginSnapshot ||
                traceInsights.marginTotal ||
                (saleUnit - unitCost) * Number(movement.quantity || 0)
              );
                const sourceLabel = traceInsights.sourceLabel || 'Sin origen de compra visible';
                const sourceReference = traceInsights.sourceReference || 'Referencia de compra pendiente';
                const sourceBatchLabel = traceInsights.batchLabel || '';
                const sourceReferenceLabel =
                  sourceBatchLabel &&
                  normalizeLookupText(sourceBatchLabel) === normalizeLookupText(sourceReference)
                    ? sourceBatchLabel
                    : sourceBatchLabel
                      ? `${sourceBatchLabel} · ${sourceReference}`
                      : sourceReference;
                const destinationLabel = traceInsights.destinationLabel || 'Destino final todavía no vinculado';
                const routeLabel = traceInsights.routeLabel || 'Todavía sin ruta asignada';
                const transportLabel = traceInsights.transportLabel || 'Todavía sin transporte asignado';
                const driverLabel = traceInsights.driverLabel || 'Todavía sin chofer asignado';
                const storageLabel = formatStorageDays(traceInsights.storageDays);
                const hasCostTrace = unitCost > 0;
                const hasSaleTrace = saleUnit > 0;
                const hasMarginTrace = hasCostTrace && hasSaleTrace;
                const economicTitle = hasMarginTrace
                  ? `Compra ${formatMoney(unitCost)} · Venta ${formatMoney(saleUnit)}`
                  : hasCostTrace
                    ? `Compra ${formatMoney(unitCost)} registrada`
                    : 'Costo y venta todavía no quedaron unidos en este evento';
                const economicHelper = hasMarginTrace
                  ? `Margen estimado ${formatMoney(marginTotal)}`
                  : hasCostTrace
                    ? 'La venta y el margen aparecen cuando esta compra se vincula a una salida.'
                    : 'Se completa cuando el origen económico y la salida quedan conectados.';

                return (
                <div key={movement.id} className={`border-b border-slate-200/70 px-4 py-4 transition-colors duration-150 last:border-b-0 ${meta.rowTone || 'bg-white hover:bg-slate-50'}`}>
                  <div className="grid gap-3 lg:grid-cols-[140px_minmax(180px,1.1fr)_150px_88px_180px_120px_120px_120px_1fr] lg:items-start lg:gap-3">
                    <div className="text-sm font-semibold text-slate-800">{formatDateTime(movement.appliedAt || movement.createdAt)}</div>

                    <div className="flex min-w-0 items-start gap-3">
                      <MovementDirectionIcon meta={meta} />
                      <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border shadow-sm ${resolveProductIconTone(buildMovementProductVisual(movement, productCategoryById))}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="h-[18px] w-[18px]">
                          <path strokeLinecap="round" strokeLinejoin="round" d={resolveProductIconPath(buildMovementProductVisual(movement, productCategoryById))} />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-slate-950">{movement.productNameSnapshot || 'Producto sin nombre'}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${meta.tone} ${meta.blink ? 'motion-safe:animate-pulse' : ''}`}>
                            {meta.label}
                          </span>
                          {(movement.productCategorySnapshot || productCategoryById[movement.productId]) ? (
                            <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-cyan-700">
                              {movement.productCategorySnapshot || productCategoryById[movement.productId]}
                            </span>
                          ) : null}
                          {movement.productSkuSnapshot ? (
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                              {movement.productSkuSnapshot}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="text-sm font-semibold text-slate-800">{resolveMovementCounterparty(movement)}</div>

                    <div className="text-sm font-bold text-slate-950">{Number(movement.quantity || 0)}</div>

                    <div>
                      <p className="text-sm font-semibold text-slate-800">{operationReasonLabel || 'Movimiento operativo'}</p>
                      <p className="mt-1 text-xs text-slate-500">{movement.note || 'Sin observación adicional'}</p>
                    </div>

                    <div className="text-sm font-semibold text-slate-800">
                      <p>{formatMoney(unitCost)}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        Total {formatMoney(movement.costTotalSnapshot || unitCost * Number(movement.quantity || 0))}
                      </p>
                    </div>

                    <div className="text-sm font-semibold text-slate-800">
                      <p>{formatMoney(saleUnit)}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        Total {formatMoney(movement.saleTotalSnapshot || saleUnit * Number(movement.quantity || 0))}
                      </p>
                    </div>

                    <div className="text-sm font-semibold text-slate-800">{formatMoney(marginTotal)}</div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-600">
                      {resolveMovementTrace(movement)}
                    </div>
                  </div>

                  <div className="mt-3 grid gap-3 lg:grid-cols-4">
                      <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Origen de compra</p>
                        <p className="mt-1 text-sm font-semibold text-slate-800">{sourceLabel}</p>
                        <p className="mt-1 text-xs text-slate-500">
                         {sourceReferenceLabel}
                        </p>
                      </div>

                    <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Depósito y destino</p>
                      <p className="mt-1 text-sm font-semibold text-slate-800">{storageLabel}</p>
                      <p className="mt-1 text-xs text-slate-500">{destinationLabel}</p>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Transporte y chofer</p>
                      <p className="mt-1 text-sm font-semibold text-slate-800">{transportLabel}</p>
                      <p className="mt-1 text-xs text-slate-500">{driverLabel}</p>
                    </div>

                      <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Resultado económico</p>
                        <p className="mt-1 text-sm font-semibold text-slate-800">
                         {economicTitle}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                         {economicHelper}
                        </p>
                      </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default StockMovementsPanel;
